import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelSmall,
    Tagline,
} from "@khanacademy/wonder-blocks-typography";
import {useQuery} from "@repo/data-gql";

import type {getSailthruNewsletters_target_emailSubscriptions as SubscriptionsFromKaid} from "./__graphql-types__/getSailthruNewsletters.ts";
import type {getSailthruNewslettersByToken_emailSettingsFromUnsubscribeToken_emailSubscriptions as SubscriptionsFromToken} from "./__graphql-types__/getSailthruNewslettersByToken.ts";
import EmailSubscriptionSwitch from "./email-subscription-switch.tsx";
import {
    SAILTHRU_NEWSLETTERS_QUERY_BY_KAID,
    SAILTHRU_NEWSLETTERS_QUERY_BY_TOKEN,
} from "./get-sailthru-newsletters-query.ts";

interface Props {
    token: string | null | undefined;
    targetKaid: string | null | undefined;
}

type EmailSubscriptions = SubscriptionsFromKaid & SubscriptionsFromToken;

export const NewslettersSection: React.FC<Props> = ({token, targetKaid}) => {
    const [showError, setShowError] = useState(false);

    // Determine which query should be active
    const shouldUseTokenQuery = Boolean(token && !targetKaid);

    // Both hooks are always called, but only one will actually execute
    const [tokenResult] = useQuery(SAILTHRU_NEWSLETTERS_QUERY_BY_TOKEN, {
        variables: {token: token || ""},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        skip: !shouldUseTokenQuery,
    });

    const [kaidResult] = useQuery(SAILTHRU_NEWSLETTERS_QUERY_BY_KAID, {
        variables: {targetKaid: targetKaid || ""},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        skip: shouldUseTokenQuery,
    });

    // Select the result from whichever query was not skipped
    const queryResult = shouldUseTokenQuery ? tokenResult : kaidResult;

    // Extract the subscription data from the used query result
    const getEmailSubscriptions = () => {
        if (
            shouldUseTokenQuery &&
            tokenResult.status === "success" &&
            tokenResult.data
        ) {
            return tokenResult.data.emailSettingsFromUnsubscribeToken
                ?.emailSubscriptions;
        }

        if (
            !shouldUseTokenQuery &&
            kaidResult.status === "success" &&
            kaidResult.data
        ) {
            return kaidResult.data.target?.emailSubscriptions;
        }

        return null;
    };

    const emailSubscriptions = getEmailSubscriptions();

    const renderContent = (
        subscriptions: EmailSubscriptions["sailthruSubscriptions"],
    ) => {
        if (subscriptions && subscriptions.length > 0) {
            return (
                <View style={styles.subscriptionsContainer}>
                    {subscriptions.map((subscription) => (
                        <EmailSubscriptionSwitch
                            key={subscription.key}
                            checked={subscription.checked}
                            label={subscription.description}
                            subscriptionKey={subscription.key}
                            showError={() => setShowError(true)}
                            clearError={() => setShowError(false)}
                            inputId={subscription.key}
                        />
                    ))}
                </View>
            );
        }
        return null;
    };

    if (queryResult.status === "loading") {
        return <CircularSpinner size="medium" />;
    }

    if (!emailSubscriptions || queryResult.status === "error") {
        return (
            <Tagline>
                {t`Oops! Looks like we don't have data available for this user.`}
            </Tagline>
        );
    }

    return (
        <View>
            <HeadingSmall style={styles.header} tag="h2">
                {t`Newsletters`}
            </HeadingSmall>
            <View style={styles.contentContainer}>
                <Body>
                    {t`Our newsletters keep you up-to-date with what's happening at Khan Academy.`}
                </Body>
                {showError && (
                    <LabelSmall style={styles.errorText}>
                        {t`Oops! Something went wrong. Please try again.`}
                    </LabelSmall>
                )}
                {renderContent(emailSubscriptions.sailthruSubscriptions)}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
        marginBlockEnd: sizing.size_160, // Replaced spacing.medium_16
    },
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_120, // Replaced spacing.small_12
    },
    subscriptionsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_080, // Replaced spacing.xSmall_8
    },
    errorText: {
        color: color.red,
        marginBlockStart: sizing.size_040, // Replaced spacing.xxxSmall_4
    },
});

export default NewslettersSection;
